import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Save, Share2, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { AppShell } from '../components/Layout';
import {
  ExaminationTable, AnalysisSummaryCard, TissueCompositionCard,
  WoundStageCard, AdviceCard, ImageModal,
} from '../components/ResultComponents';
import { usePatient } from '../contexts/PatientContext';
import { API_URL, API_KEY } from '../config';

export default function ResultScreen() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { patient } = usePatient();

  const result = location.state?.result;

  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [saved,     setSaved]     = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [email,     setEmail]     = useState('');

  const now = useMemo(() => new Date(), []);

  useEffect(() => {
    const e = localStorage.getItem('userEmail');
    if (e) setEmail(e);
  }, []);

  const reportList  = result?.Report             ?? [];
  const woundTissue = result?.['Wound Tissue (%)'] ?? [];
  const stageList   = result?.Stage              ?? [];
  const adviceList  = result?.Advice             ?? [];
  const analysisId  = result?.AnalysisID         ?? 'N/A';
  const imgUrl      = result?.Img_URL            ?? 'N/A';

  const patientName = patient?.full_name   || result?.full_name   || 'N/A';
  const patientId   = patient?.patient_id  || result?.patient_id  || 'N/A';

  if (!result) {
    return (
      <AppShell title="Analysis Result">
        <div
          style={{
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 24px',
          }}
        >
          <div className="loading-wrap" style={{ textAlign: 'center' }}>
            <AlertCircle size={40} style={{ color: 'var(--border)' }} />
            <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>
              No result data found. Please run a new analysis.
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/new-analysis')}>
              New Analysis
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const storeResult = async () => {
    if (!email) { setError('Email not found. Please log in again.'); return; }
    const username = localStorage.getItem('username');

    const payload = {
      email,
      username,
      timestamp:  now.toISOString(),
      analysisId,
      img_url:    imgUrl,
      analysis:   reportList,
      tissue:     woundTissue,
      stage:      stageList,
      full_name:  patientName,
      patient_id: patientId,
    };

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/results`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setSaved(true);
        setTimeout(() => navigate('/dashboard'), 1600);
      } else {
        setError(data.message || 'Failed to save result.');
      }
    } catch {
      setError('Cannot connect to server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell title="Analysis Result">
      {/* ── Centered page wrapper ── */}
      <div
        style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 24px 60px',
        }}
      >
        {/* ── Constrained content column ── */}
        <div style={{ width: '100%', maxWidth: 780 }}>

          {/* ── Page header ── */}
          <div
            className="page-header"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16,
              marginBottom: 28,
            }}
          >
            <div>
              <h1 className="page-title" style={{ marginBottom: 4 }}>Analysis Result</h1>
              <p className="page-subtitle" style={{ margin: 0 }}>
                {now.toLocaleDateString()} —{' '}
                {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-secondary" onClick={() => navigate('/new-analysis')}>
                <ArrowLeft size={15} /> New Analysis
              </button>
              <button
                className="btn btn-outline"
                onClick={() => navigate('/share-result', { state: { result } })}
              >
                <Share2 size={15} /> Share
              </button>
              <button
                className="btn btn-primary"
                onClick={storeResult}
                disabled={loading || saved}
              >
                {loading
                  ? <span className="spinner" />
                  : saved
                  ? <><CheckCircle size={15} /> Saved</>
                  : <><Save size={15} /> Store Result</>
                }
              </button>
            </div>
          </div>

          {/* ── Alerts ── */}
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              <AlertCircle size={16} />{error}
            </div>
          )}
          {saved && (
            <div className="alert alert-success" style={{ marginBottom: 16 }}>
              <CheckCircle size={16} />Result saved! Redirecting to dashboard…
            </div>
          )}
          {patientId !== 'N/A' && !saved && (
            <div className="alert alert-info" style={{ marginBottom: 16 }}>
              This result will be saved as a new visit for{' '}
              <strong>{patientName}</strong> (ID: {patientId})
            </div>
          )}

          {/* ── Result sections ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <ExaminationTable
              patientName={patientName}
              patientId={patientId}
              date={now.toLocaleDateString()}
              time={now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              analysisId={analysisId}
              imgUrl={imgUrl !== 'N/A' ? imgUrl : null}
              onImageClick={() => setShowImage(true)}
            />
            <AnalysisSummaryCard reportList={reportList} />
            <TissueCompositionCard woundTissue={woundTissue} />
            <WoundStageCard woundStageData={stageList} />
            <AdviceCard adviceList={adviceList} />
          </div>

        </div>
      </div>

      {showImage && imgUrl !== 'N/A' && (
        <ImageModal imgUrl={imgUrl} onClose={() => setShowImage(false)} />
      )}
    </AppShell>
  );
}
